import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.status === 200) {
        toast({
          title: 'Message Sent Successfully!',
          description: 'Thank you for your interest. I will get back to you soon at ' + formData.email,
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'Error',
        description: 'Failed to send message. Please email me directly at hr_vassileva@yahoo.com',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: 'hr_vassileva@yahoo.com',
      link: 'mailto:hr_vassileva@yahoo.com'
    },
    {
      icon: Phone,
      label: 'Phone',
      value: '+359 878 841 332',
      link: 'tel:+359878841332'
    },
    {
      icon: MapPin,
      label: 'Location',
      value: 'Sofia, Bulgaria',
      link: null
    }
  ];

  return (
    <section id="contact" className="py-24 bg-slate-800">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block px-4 py-2 bg-sky-600 text-white rounded-full text-sm font-semibold mb-6">
            Get In Touch
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Start Your Skating Journey
          </h2>
          <p className="text-xl text-slate-300 mb-4">
            Have questions or ready to begin training? Reach out and let's discuss how we can help you achieve your skating goals.
          </p>
          <p className="text-lg text-white font-semibold">
            Contact me at: <a href="mailto:hr_vassileva@yahoo.com" className="text-sky-400 hover:text-sky-300 underline">hr_vassileva@yahoo.com</a>
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-8">
              Contact Information
            </h3>
            
            <div className="space-y-6 mb-10">
              {contactInfo.map((info, index) => {
                const Icon = info.icon;
                return (
                  <Card key={index} className="p-6 bg-slate-700 border-slate-600 hover:border-sky-400 transition-colors duration-300">
                    <div className="flex items-center space-x-4">
                      <div className="bg-sky-600 p-3 rounded-lg">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <div className="text-sm text-slate-400 font-medium">
                          {info.label}
                        </div>
                        {info.link ? (
                          <a
                            href={info.link}
                            className="text-white font-semibold hover:text-sky-400 transition-colors"
                          >
                            {info.value}
                          </a>
                        ) : (
                          <div className="text-white font-semibold">
                            {info.value}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Additional Info */}
            <div className="bg-slate-700 rounded-2xl p-8 border border-slate-600">
              <h4 className="text-xl font-bold text-white mb-4">
                Why Choose Hristina?
              </h4>
              <ul className="space-y-3 text-slate-300">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-sky-400 rounded-full mr-3 mt-2"></div>
                  <span>International competition experience</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-sky-400 rounded-full mr-3 mt-2"></div>
                  <span>Trained by world-renowned coaches</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-sky-400 rounded-full mr-3 mt-2"></div>
                  <span>Personalized coaching approach</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-sky-400 rounded-full mr-3 mt-2"></div>
                  <span>Comprehensive technique and artistry development</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <Card className="p-8 bg-slate-700 border-slate-600">
            <h3 className="text-2xl font-bold text-white mb-6">
              Send a Message
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2">
                  Full Name *
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  className="w-full bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                  Email Address *
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  className="w-full bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-slate-300 mb-2">
                  Phone Number
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1 (555) 123-4567"
                  className="w-full bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-slate-300 mb-2">
                  Message *
                </label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell me about your skating goals and experience..."
                  rows={6}
                  className="w-full bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-sky-600 hover:bg-sky-700 text-white py-6 text-lg font-semibold"
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;